import type { Entity } from '@tunasong/schemas'

export type SystemTag =
  | 'sys:background-image'
  | 'sys:header-image'
  | 'sys:ai-chat'
  | 'sys:template'
  | 'sys:resource'
  // Resource is a derived entity, i.e., it is created from another entity
  | 'sys:derived'
  | 'sys:ai-generated'
  | 'sys:scaffold'

export const SystemTags = {
  /** Background Image */
  BackgroundImage: 'sys:background-image',
  /** Image that is used for the header in certain views */
  HeaderImage: 'sys:header-image',
  /** AI Chat entity */
  AIChat: 'sys:ai-chat',
  /** Template */
  Template: 'sys:template',
  /** Use for hidden resources, e.g., for imports, links, and so on */
  Resource: 'sys:resource',
  /**
   * Provenance. Permanent, factual, retrospective: "where did this come from?" Nothing ever
   * removes it, and no behaviour is keyed to it — see `Scaffold`.
   */
  AIGenerated: 'sys:ai-generated',
  /**
   * Status. Mutable and the user's call: "is this the work yet?" Generated media is born with
   * both markers; keeping it removes only this one. Every behavioural difference — the block's
   * rail and dashed border, discard affordances, print and export exclusion, library visibility,
   * list badges — hangs here rather than on provenance, so that a sketch is not a sketch forever.
   */
  Scaffold: 'sys:scaffold',
} satisfies Record<string, SystemTag>

export type Tag = SystemTag | string

/** * Entity is system entity, i.e., it is tagged with sys:* */
export const isTaggedSysEntity = (entity: Pick<Entity, 'tags'>, tag?: SystemTag) =>
  Boolean(entity.tags?.find(t => (tag ? t === tag : typeof t === 'string' && t.startsWith('sys:'))))

/**
 * Currently we hide all system entities from several views, unless the tag is on the exception
 * list.
 *
 * `AIGenerated` is an exception because it is provenance, not status: media the user has kept is
 * theirs and belongs in search, recents and the tree like anything else. What keeps scaffolding
 * out of those views is `Scaffold`, which is not on the list.
 */
const visibleSysTags: SystemTag[] = [SystemTags.Template, SystemTags.AIGenerated]

export const isHiddenSysEntity = (entity: Pick<Entity, 'tags'>) =>
  Boolean(
    entity.tags?.find(
      t => typeof t === 'string' && t.startsWith('sys:') && !visibleSysTags.some(visible => visible === t)
    )
  )

/** Provenance: where this came from. Permanent — never clear it. */
export const isAIGenerated = (entity: Pick<Entity, 'tags'>) =>
  Boolean(entity.tags?.find(t => t === SystemTags.AIGenerated || t === 'tunabrain'))

/**
 * Status: this is scaffolding the user has not yet accepted as the work. Mutable — "Keep" clears
 * it, "Return to sketch" restores it. Entities created before this tag existed carry provenance
 * only and therefore read as kept, which is deliberate: backfilling would restyle months of
 * existing documents and pull media out of prints that used to include it.
 */
export const isScaffold = (entity: Pick<Entity, 'tags'>) => Boolean(entity.tags?.find(t => t === SystemTags.Scaffold))

export const isSystemTag = (tag: string): tag is SystemTag =>
  (typeof tag === 'string' && tag.startsWith('sys:')) || tag === 'tunabrain' || tag === SystemTags.AIGenerated
